// --------------------------------------------
//       ALM Export Non-Maturity Method
// --------------------------------------------

import { styleRangeBasedOnCalibrationStatus } from '@components/export/spread/helpers';
import * as GC from '@grapecity/spread-sheets';
import { ILookupProductBetaNonMaturity } from '@models/export-alm/lookup-product-beta';
import { INonMaturitySegment } from '@models/export-alm/segment';
import { CalibrationStatus } from '@models/record-status';
import { tryJsonParse as tryParseJson } from '@shared/json-parse';
import { tryParseNumber } from '@shared/numbers'; 
import { borders, colours, DATE_FORMATTER, FONT_NAME, FONT_SIZE, PERCENTAGE_FORMATTER } from '@shared/spread/spread-settings';
import { baseSheets, configureSheet } from './export-alm-spread';

const cpTableHeaders = ['SecID', 'AccountNum', 'DepProductMn', 'EOPDate', 'OriginalBook', 'AvgBook', 'EOPBook', 'CurrCoupon'];

export const getSheet_lookupsNonMaturity = (lookups: ILookupProductBetaNonMaturity[]): GC.Spread.Sheets.Worksheet => {
  // Create Sheet
  const sheet = new GC.Spread.Sheets.Worksheet(baseSheets[0].name); // 'Non-maturity Assump Tables'

  sheet.suspendPaint();
  configureSheet(sheet, baseSheets[0]);

  const numPeriods = Math.max(...lookups.map(l => l.RunOff).map(ro => ro.VintageRunOffScheduleItems.length));

  setStyling_lookupsNonMaturity(sheet, lookups, numPeriods);

  //   BETA LOOKUPS
  sheet.getCell(1, 1).text('Lookups');
  sheet.getCell(2, 1).text('NMD_Betas');
  const productMnemonicsRate = lookups.map(l => `${l.ProductMnemonic.toString()}_Rate`);
  const betaCoefficients = lookups.map(l => l.BetaCoefficient);
  sheet.setArray(3, 1, productMnemonicsRate);
  sheet.setArray(3, 2, betaCoefficients);

  // Adding Named Range For Beta Look up
  sheet.clearSelection();
  sheet.addSelection(1, 1, 2 + productMnemonicsRate.length, 2);
  const betaLookUpFormula = GC.Spread.Sheets.CalcEngine.rangeToFormula(sheet.getSelections()[0]);
  sheet.addCustomName('EMP_NMD_Betas', `'${sheet.name()}'!${betaLookUpFormula}`, 1, 1, '');
  sheet.clearSelection();

  //   DECAY FACTORS LOOKUPS
  sheet.getCell(lookups.length + 4, 1).text('Lookups');
  sheet.getCell(lookups.length + 5, 1).text('NMD_Runoff_SMM');

  const productMnemonics = lookups.map(l => `${l.ProductMnemonic}`);
  sheet.setArray(lookups.length + 7, 1, productMnemonics);

  const periods = new Array(numPeriods).fill(1).map((e, i) => e + i);
  sheet.setArray(lookups.length + 6, 2, [periods]);

  lookups.forEach((lookup, i) => {
    if (lookup.RunOff) {
      const cashFlows = lookup.RunOff.VintageRunOffScheduleItems.map(item => item.PrinCashFlowSchedPercent);
      sheet.setArray(lookups.length + 7 + i, 2, [cashFlows]);
    }
  });

  // Adding Named Ranges for Runoff SMM table
  sheet.addSelection(lookups.length + 4, 1, 3 + lookups.length, periods.length + 1);
  const runOffLookupFormula = GC.Spread.Sheets.CalcEngine.rangeToFormula(sheet.getSelections()[0]);
  sheet.addCustomName('EMP_NMD_Runoff_SMM', `'${sheet.name()}'!${runOffLookupFormula}`, lookups.length + 4, 1, '');
  sheet.clearSelection();

  // Styling based on Calibration Status
  lookups.forEach((lookup, i) => {
    if (lookup.CalibrationStatus !== 'A') {
      styleRangeBasedOnCalibrationStatus(sheet.getRange(3 + i, 2, 1, 1), lookup.CalibrationStatus as CalibrationStatus);
    }

    // <9377> RunOffs styled based on Decay and Stable Balance calibrations' statuses
    const { DecayCalibrationStatus, StableBalanceCalibrationStatus } = lookup.RunOff;
    if (DecayCalibrationStatus !== 'A' || StableBalanceCalibrationStatus !== 'A') {
      const range = sheet.getRange(lookups.length + 7 + i, 2, 1, numPeriods);

      if (DecayCalibrationStatus === 'R' || StableBalanceCalibrationStatus === 'R') {
        range.backColor(colours.REJECTED_BG);
      } else {
        range.backColor(colours.PROPOSED_BG);
      }
    }
  });

  return sheet;
};

const setStyling_lookupsNonMaturity = (sheet: GC.Spread.Sheets.Worksheet, lookups: ILookupProductBetaNonMaturity[], numPeriods: number) => {
  sheet.setRowCount(Math.max(30, 9 + 2 * lookups.length));
  sheet.setColumnCount(Math.max(14, numPeriods + 3));

  // Clear previous data and styling
  const fullRange = sheet.getRange(0, 0, sheet.getRowCount(), sheet.getColumnCount());
  fullRange.clear(GC.Spread.Sheets.StorageType.style);
  fullRange.clear(GC.Spread.Sheets.StorageType.data);

  sheet.setColumnWidth(0, 30);

  sheet.frozenColumnCount(2);
  sheet.options.frozenlineColor = 'transparent';

  // -------------------------------------------------------------------------------------
  //   BETA LOOKUPS
  // -------------------------------------------------------------------------------------
  const tableRange = sheet.getRange(3, 1, lookups.length, 2);
  const titleRange = sheet.getRange(1, 1, 1, 1);
  const subtitleRange = sheet.getRange(2, 1, 1, 1);
  // const productsRange = sheet.getRange(3, 1, lookups.length, 1);

  fullRange
    .locked(true)
    .vAlign(GC.Spread.Sheets.VerticalAlign.center)
    .cellPadding(`0 ${parseInt(FONT_SIZE, 10) / 2}`)
    .wordWrap(true)
    .font(`${parseInt(FONT_SIZE, 10)}pt ${FONT_NAME}`);

  titleRange
    .backColor(colours.Lookups.mnemonicBackground)
    .font(`bold ${Number(FONT_SIZE) + 3}pt ${FONT_NAME}`)
    .setBorder(borders.BLACK_THIN, { top: true, left: true, right: true });
  subtitleRange
    .backColor(colours.Lookups.thirdDimensionBackground)
    .font(`${Number(FONT_SIZE) + 1}pt ${FONT_NAME}`)
    .setBorder(borders.BLACK_THIN, { left: true, right: true });

  for (let c = 0; c < lookups.length; c++) {
    sheet.getCell(3 + c, 1).backColor(c % 2 === 0 ? colours.Lookups.columnLightBackground : colours.Lookups.columnBackground);
    sheet.getCell(3 + c, 2).backColor(c % 2 === 0 ? colours.WHITE : colours.WHITE2);
  }

  tableRange.setBorder(borders.BLACK_THIN, { outline: true });

  // -------------------------------------------------------------------------------------
  //   DECAY FACTORS LOOKUPS
  // -------------------------------------------------------------------------------------
  const tableRangeDF = sheet.getRange(lookups.length + 6, 1, lookups.length + 1, numPeriods + 1);
  const titleRangeDF = sheet.getRange(lookups.length + 4, 1, 1, 1); // Look ups
  const subtitleRangeDF = sheet.getRange(lookups.length + 5, 1, 1, 1); // NMD_Runoff_SMM
  // const productsRangeDF = sheet.getRange(lookups.length + 7, 1, lookups.length, 1);
  const periodsRange = sheet.getRange(lookups.length + 6, 2, 1, numPeriods);
  const runOffsRange = sheet.getRange(lookups.length + 7, 2, lookups.length, numPeriods);

  titleRangeDF
    .backColor(colours.Lookups.mnemonicBackground)
    .font(`bold ${Number(FONT_SIZE) + 3}pt ${FONT_NAME}`)
    .setBorder(borders.BLACK_THIN, { top: true, left: true, right: true });
  subtitleRangeDF
    .backColor(colours.Lookups.thirdDimensionBackground)
    .font(`${Number(FONT_SIZE) + 1}pt ${FONT_NAME}`)
    .setBorder(borders.BLACK_THIN, { left: true, right: true });

  periodsRange.backColor(colours.Lookups.columnLightBackground);

  runOffsRange.formatter(PERCENTAGE_FORMATTER).hAlign(GC.Spread.Sheets.HorizontalAlign.right);

  for (let c = 0; c < lookups.length; c++) {
    sheet
      .getCell(lookups.length + 7 + c, 1)
      .backColor(c % 2 === 0 ? colours.Lookups.columnLightBackground : colours.Lookups.columnBackground);

    sheet.getRange(lookups.length + 7 + c, 2, 1, numPeriods).backColor(c % 2 === 0 ? colours.WHITE : colours.WHITE2);
  }

  tableRangeDF.setBorder(borders.BLACK_THIN, { outline: true });
};

export const getSheet_NonMaturityCPTables = (
  lookups: ILookupProductBetaNonMaturity[],
  segments: INonMaturitySegment[]
): GC.Spread.Sheets.Worksheet => {
  // Create Sheet
  const sheet = new GC.Spread.Sheets.Worksheet(baseSheets[1].name); // 'Non-maturity CP Table'

  sheet.suspendPaint();
  configureSheet(sheet, baseSheets[1]);

  // Get dynamic columns from UserFields
  const userFieldColumns = Array.from(
    new Set(segments.map(s => Object.keys(tryParseJson(s.UserFields)[0] || [])).reduce((a, b) => a.concat(b), []))
  ).sort();
  const allHeaders = [...cpTableHeaders, ...userFieldColumns];

  setStyling_NonMaturityCPTables(sheet, lookups, allHeaders);

  // Set headers using static + dynamic column names
  sheet.setArray(0, 0, [allHeaders]);

  let i = 1;
  lookups.forEach(lookup => {
    if (lookup.Segments.length > 0) {
      for (let s = 0; s < lookup.Segments.length; s++) {
        const segment = lookup.Segments[s];

        const { SecID, AccountNum, DepProductMn, EOPDate, OriginalBook, AvgBook, EOPBook, CurrCoupon, UserFields } = segment;

        const rowFields = tryParseJson(UserFields)[0] || [];
        const rowValues = [];
        userFieldColumns.forEach(key => {
          const val = tryParseNumber(rowFields[key]);
          rowValues.push(val === 'NULL' ? '':val);
        });

        sheet.setArray(i, 0, [
          [SecID, AccountNum, DepProductMn, new Date(EOPDate), OriginalBook, AvgBook, EOPBook, CurrCoupon, ...rowValues]
        ]);
        i++;
      }
    }
  });

  sheet.setColumnWidth(0, 350); // SecID column
  sheet.setColumnWidth(2, 60); // AccountNum column
  sheet.setColumnWidth(2, 300); // DepProductMn column
  sheet.setColumnWidth(3, 90); // EOPDate column

  return sheet;
};

const setStyling_NonMaturityCPTables = (
  sheet: GC.Spread.Sheets.Worksheet,
  lookups: ILookupProductBetaNonMaturity[],
  allHeaders: string[]
) => {
  const numOfSegments = lookups.reduce((acc, lookup) => acc + lookup.Segments.length, 0);
  sheet.setRowCount(Math.max(31, numOfSegments + 1));
  sheet.setColumnCount(allHeaders.length);

  // Clear previous data and styling
  const fullRange = sheet.getRange(0, 0, sheet.getRowCount(), sheet.getColumnCount());
  fullRange.clear(GC.Spread.Sheets.StorageType.style);
  fullRange.clear(GC.Spread.Sheets.StorageType.data);

  sheet.frozenRowCount(1);
  sheet.options.frozenlineColor = 'transparent';

  fullRange
    .locked(true)
    .vAlign(GC.Spread.Sheets.VerticalAlign.center)
    .cellPadding(`0 ${parseInt(FONT_SIZE, 10) / 2}`)
    .wordWrap(true)
    .font(`${parseInt(FONT_SIZE, 10)}pt ${FONT_NAME}`);

  const headerRange = sheet.getRange(0, 0, 1, sheet.getColumnCount());
  headerRange
    .backColor(colours.Lookups.rowBackground)
    .font(`bold ${Number(FONT_SIZE) + 1}pt ${FONT_NAME}`)
    .setBorder(borders.ALM_EXPORT_TABLE, { outline: true });

  const bodyRange = sheet.getRange(1, 0, numOfSegments, sheet.getColumnCount());
  bodyRange.setBorder(borders.ALM_EXPORT_TABLE, { outline: true });

  const currCouponRange = sheet.getRange(1, cpTableHeaders.indexOf('CurrCoupon'), numOfSegments, 1);
  currCouponRange.formatter(PERCENTAGE_FORMATTER).hAlign(GC.Spread.Sheets.HorizontalAlign.right);

  const eopDateRange = sheet.getRange(1, cpTableHeaders.indexOf('EOPDate'), numOfSegments, 1);
  eopDateRange.formatter(DATE_FORMATTER);

  for (let c = 0; c < numOfSegments; c++) {
    sheet.getRange(1 + c, 0, 1, sheet.getColumnCount()).backColor(c % 2 === 0 ? colours.WHITE : colours.WHITE2);
  }

  // Vertical Borders
  allHeaders.forEach((header, i) => {
    const range = sheet.getRange(0, i, numOfSegments + 1, 1);
    const blackBorder: boolean = i === cpTableHeaders.length - 1 && allHeaders.length > cpTableHeaders.length;
    range.setBorder(blackBorder ? borders.BLACK_THIN : borders.ALM_EXPORT_TABLE, { right: true });
  });
};
